import type {
  ExampleParagraph,
  ParagraphContext,
  StyleProfile,
  StyleRule,
  StyleTemplateKey,
  WritingStyle
} from '@/types/writingStyle';

const TEMPLATE_LABELS: Record<StyleTemplateKey, string> = {
  orwell: 'Orwellian Clarity',
  hemmingway: 'Hemingway Minimalism',
  austen: 'Austen Social Wit',
  tolkien: 'Tolkien High Fantasy',
  custom: 'Custom'
};

const CONTEXT_LABELS: Record<ParagraphContext, { label: string; icon: string }> = {
  description: { label: 'Description', icon: '🖼️' },
  action: { label: 'Action', icon: '⚡' },
  dialogue: { label: 'Dialogue', icon: '💬' },
  introspection: { label: 'Introspection', icon: '🧠' },
  exposition: { label: 'Exposition', icon: '📘' },
  general: { label: 'General', icon: '✍️' }
};

export const writingStyleHelper = {
  generateId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `ws_${Math.random().toString(36).slice(2, 10)}`;
  },

  getTemplateLabel(template: StyleTemplateKey | undefined): string {
    if (!template) {
      return TEMPLATE_LABELS.custom;
    }
    return TEMPLATE_LABELS[template] ?? TEMPLATE_LABELS.custom;
  },

  getParagraphContextMeta(context: ParagraphContext | undefined): { label: string; icon: string } {
    if (!context) {
      return CONTEXT_LABELS.general;
    }
    return CONTEXT_LABELS[context] ?? CONTEXT_LABELS.general;
  },

  createEmptyProfile(): StyleProfile {
    return {
      name: '',
      essence: '',
      pov: 'third-person-limited',
      tense: 'past'
    };
  },

  createEmptyParagraph(styleId: string): ExampleParagraph {
    const now = new Date().toISOString();
    return {
      id: this.generateId(),
      styleId,
      label: '',
      text: '',
      context: 'general',
      uiState: {
        isExpanded: false
      },
      createdAt: now,
      updatedAt: now
    };
  },

  createEmptyRule(styleId: string): StyleRule {
    const now = new Date().toISOString();
    return {
      id: this.generateId(),
      styleId,
      rule: '',
      rationale: '',
      severity: 'strong-guideline',
      goodExample: '',
      badExample: '',
      uiState: {
        isExpanded: false
      },
      createdAt: now,
      updatedAt: now,
      notes: ''
    };
  },

  createEmptyWritingStyle(manuscriptId: string): WritingStyle {
    const now = new Date().toISOString();
    return {
      id: this.generateId(),
      manuscriptId,
      styleProfile: this.createEmptyProfile(),
      exampleParagraphs: [],
      styleRules: [],
      notes: '',
      createdAt: now,
      updatedAt: now
    };
  }
};

